import { z } from 'zod';

import { ComponentValidator, ComponentPayloadValidator } from '../Component';

import { synchronize, synchronizePowerFlowDirection } from '../helpers';

import { IsolationVoltage } from '../fields/IsolationVoltage';

import { AbstractPort, Port } from '../fields/AbstractPort';

import { ComponentDefinition } from '../../types/ComponentDefinition';

const port = AbstractPort.extend({
    AC: Port.AC.extend({
        controlMethods: Port.controlMethods.validator,
    }).default({}),
    DC: Port.DC.extend({
        controlMethods: Port.controlMethods.validator,
    }).default({}),
}).default({
    powerFlowDirection: 'bidirectional',
});

const electrical = z
    .object({
        ports: z.tuple([port, port]).default([undefined, undefined]),
        features: z
            .object({
                feedbackSignal: z.boolean().optional().default(false),
            })
            .default({}),
        isolationVoltage: IsolationVoltage.validator,
    })
    .default({});

const ContactorBase = z.object({
    type: z.literal('contactor').default('contactor'),
    electrical: synchronize(synchronizePowerFlowDirection(electrical), {
        'ports.0': ['ports.1'],
    }),
});

export const ContactorValidator = ComponentValidator.merge(ContactorBase);
export const ContactorPayloadValidator = ComponentPayloadValidator.merge(ContactorBase);

export const ContactorDefinition: ComponentDefinition = {
    type: 'contactor',
    name: 'Contactor',
    plural: 'Contactors',
    indicator: 'K',
    hasDynamicNumberOfPorts: false,
    interoperabilityDataModel: true,
    actsAsSwitchgear: true,
    liveData: {
        enabled: true,
        showGraphs: false,
    },
    ports: {
        min: 2,
        max: 2,
        powerFlowDirections: ['input', 'output', 'bidirectional'],
        displayAsSinglePort: true,
    },
    validators: {
        component: ContactorValidator,
        payload: ContactorPayloadValidator,
    },
};

export type Contactor = z.infer<typeof ContactorValidator>;
export type ContactorPayload = z.infer<typeof ContactorPayloadValidator>;
